import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';
import { MilestoneMap1, Objective1 } from '../../../types';
import { countObjectiveStatuses, formatChildStatusCounts } from '../../../utils/statusSummaryUtils';
import { getVariantFromStatusCounts, createVariantMetadata } from '../../utils/iconVariants';

/**
 * Helper function to safely collect all objectives from the milestone map tree structure
 * Uses safe traversal with null checking to prevent crashes
 * Universal for both tree types since it just recursively finds all objectives
 */
function getAllObjectivesFromTree(rootNode: DataNode<any>): Objective1[] {
  const objectives: Objective1[] = [];

  function traverse(node: DataNode<any>) {
    // If this node has objective data, collect it
    if (node.data && (node.type === 'Objective' || node.type === 'MajorObjective')) {
      objectives.push(node.data as Objective1);
    }

    // ✅ Safe traversal with null checking to prevent crashes
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  traverse(rootNode);
  return objectives;
}

/**
 * Generate default label for Milestone Map nodes
 */
export const generateMilestoneMapDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<MilestoneMap1>,
  context: LabelContext,
): string => {
  const altLabels = generateMilestoneMapAltLabels(dataNode, context);
  const defaultLabel = altLabels.find((label) => label.key === 'default');
  return defaultLabel?.value || 'Milestone Map';
};

/**
 * Generate alt labels for Milestone Map nodes
 */
export const generateMilestoneMapAltLabels: LabelGenerator = (
  dataNode: DataNode<MilestoneMap1>,
  context: LabelContext,
): AltLabel[] => {
  const milestoneMap = dataNode.data;

  // ✅ Validate input data
  if (!milestoneMap?.title) {
    return [
      {
        key: 'default',
        selectOption: 'Default',
        value: 'Milestone Map',
      },
    ];
  }

  const title = milestoneMap.title;

  try {
    // ✅ Safe objective collection with error handling - works for both tree types
    const allObjectives = getAllObjectivesFromTree(dataNode);
    const statusCounts = countObjectiveStatuses(allObjectives);

    // ✅ Set icon variant based on status counts
    const variant = getVariantFromStatusCounts(statusCounts);
    if (variant) {
      // Update the dataNode metadata to include the variant for icon rendering
      dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
    }

    // Generate status summaries for different formats
    const statusSummary = formatChildStatusCounts(statusCounts, 'default');
    const shortStatusSummary = formatChildStatusCounts(statusCounts, 'short');

    const labels: AltLabel[] = [];

    // Default: [title] (N green, N yellow, N red, N planned, N tbd) - omit status if none
    let defaultValue = title;
    if (statusSummary) {
      defaultValue = `${title} (${statusSummary})`;
    }

    labels.push({
      key: 'default',
      selectOption: 'Default: Title (N green, N yellow, N red, N planned, N tbd)',
      value: defaultValue,
    });

    // Short: [title] (N g, N y, N r, N p, N tbd)
    let shortValue = title;
    if (shortStatusSummary) {
      shortValue = `${title} (${shortStatusSummary})`;
    }

    labels.push({
      key: 'short',
      selectOption: 'Short: Title (N g, N y, N r, N p, N tbd)',
      value: shortValue,
    });

    // Title-only: [title]
    labels.push({
      key: 'title-only',
      selectOption: 'Title-only',
      value: title,
    });

    return labels;
  } catch (error) {
    console.warn('Error generating milestone map labels:', error);
    // ✅ Fallback to title-only labels if complex generation fails
    return [
      {
        key: 'default',
        selectOption: 'Default',
        value: title,
      },
      {
        key: 'title-only',
        selectOption: 'Title-only',
        value: title,
      },
    ];
  }
};
